import { useMemo, useState, type ChangeEvent } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { canAccessFinanceRoute } from "@/finance/financeNav";
import { trpc } from "@/lib/trpc";
import { FinanceAccessDeniedState, FinancePageHeader } from "@/components/finance/ui";
import {
  FINANCE_IMPORT_BASE_COLUMNS,
  FINANCE_IMPORT_DESTINATION_LABELS,
  FINANCE_IMPORT_TEMPLATE_SHEETS,
  type FinanceImportPreviewResult,
  type FinanceImportCommitResult,
} from "@shared/finance/importPreview";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  FileSpreadsheet,
  History,
  ListChecks,
  Loader2,
  RotateCcw,
  ShieldCheck,
  TableProperties,
  Upload,
  XCircle,
} from "lucide-react";

const sheets = [...FINANCE_IMPORT_TEMPLATE_SHEETS];
const invoiceColumns = [...FINANCE_IMPORT_BASE_COLUMNS];

const previewSteps = [
  "Descargar plantilla Excel",
  "Elegir archivo XLSX o CSV",
  "Hacer clic en Validar archivo",
  "Corregir o excluir filas con problemas",
  "Seleccionar filas válidas para importar",
  "Confirmar importación — acción definitiva",
  "Revisar resumen del batch importado",
];

const controls = [
  "Bloquear formulas que empiecen por =, +, - o @",
  "Validar proveedor por nombre/CIF",
  "Mapear instalaciones canonicas de App ODS",
  "Detectar duplicados por proveedor, numero, fecha y total",
  "Registrar importBatchId y auditoria en una fase posterior",
  "Permitir rollback logico solo despues del diseno de escritura",
];

function AccessDenied() {
  return (
    <FinanceAccessDeniedState
      title="Acceso no disponible"
      message="Importaciones financieras todavia no esta disponible para tu perfil."
    />
  );
}

function formatCents(value: number | null): string {
  if (value == null) return "-";
  return (value / 100).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("No se pudo leer el archivo."));
    reader.readAsDataURL(file);
  });
}

function PreviewSummary({ preview }: { preview: FinanceImportPreviewResult }) {
  const items = [
    ["Filas", preview.summary.totalRows],
    ["Validas", preview.summary.validRows],
    ["Bloqueadas", preview.summary.blockedRows],
    ["Avisos", preview.summary.warningRows],
    ["Expediente", preview.summary.byDestination.installation],
    ["Almacen", preview.summary.byDestination.warehouse],
    ["Generales", preview.summary.byDestination.general_overhead],
    ["Sin imputar", preview.summary.byDestination.unallocated],
  ];
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-md border px-3 py-2">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold">{value}</p>
        </div>
      ))}
    </div>
  );
}

export default function FinanceImportsView() {
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/imports", user.role) : false;
  const [fileName, setFileName] = useState<string | null>(null);
  const [filePayload, setFilePayload] = useState<{ fileName: string; mimeType: string | null; base64: string } | null>(null);
  const [preview, setPreview] = useState<FinanceImportPreviewResult | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [commitResult, setCommitResult] = useState<FinanceImportCommitResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showCommitConfirm, setShowCommitConfirm] = useState(false);
  const batchesQuery = trpc.financeImportPreview.listBatches.useQuery();
  const previewMutation = trpc.financeImportPreview.preview.useMutation({
    onSuccess: (result) => {
      setPreview(result);
      setCommitResult(null);
      setSelectedRows(result.rows.filter((row) => row.canImportLater && row.issues.length === 0).map((row) => row.rowNumber));
      setFileError(null);
    },
    onError: (error) => setFileError(error.message),
  });
  const commitMutation = trpc.financeImportPreview.commit.useMutation({
    onSuccess: (result) => {
      setCommitResult(result);
      setFileError(null);
    },
    onError: (error) => setFileError(error.message),
  });

  const rows = preview?.rows ?? [];
  const visibleIssues = useMemo(
    () => rows.flatMap((row) => row.issues.map((issue) => ({ ...issue, rowNumber: issue.rowNumber ?? row.rowNumber }))).slice(0, 12),
    [rows],
  );

  if (!allowed) return <AccessDenied />;

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setPreview(null);
    setCommitResult(null);
    setSelectedRows([]);
    setFilePayload(null);
    setFileError(null);
    if (!file) return;
    setFileName(file.name);
    if (!/\.(xlsx|csv|txt)$/i.test(file.name)) {
      setFileError("Formato no soportado. Usa XLSX o CSV.");
      return;
    }
    const base64 = await fileToBase64(file);
    setFilePayload({ fileName: file.name, mimeType: file.type || null, base64 });
  };

  const handleValidate = () => {
    if (!filePayload) return;
    previewMutation.mutate(filePayload);
  };

  const toggleRow = (rowNumber: number) => {
    setSelectedRows((current) => current.includes(rowNumber)
      ? current.filter((value) => value !== rowNumber)
      : [...current, rowNumber].sort((a, b) => a - b));
  };

  const commitSelectedRows = () => {
    if (!filePayload || !selectedRows.length) return;
    commitMutation.mutate({
      ...filePayload,
      selectedRowNumbers: selectedRows,
      confirm: "COMMIT_VALID_FINANCE_IMPORT_ROWS",
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          eyebrowIcon={FileSpreadsheet}
          title="Importaciones"
          description="Importa facturas y gastos desde XLSX o CSV. El flujo de validación previene escrituras accidentales y requiere confirmación explícita de cada fila."
          badgeText="Preview sin escritura"
        />

        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="flex flex-col gap-3 px-4 py-4 text-sm text-amber-950 md:flex-row md:items-start">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="space-y-1">
              <p className="font-medium">Sin importador con escritura en esta fase</p>
              <p>
                No se importa SQLite de Gines, no se usa XLSM, no se ejecutan macros y no se escriben datos historicos. El commit de datos requiere autorizacion posterior.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="h-4 w-4" />
              Preview XLSX/CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <a
                href="/api/finance/import/template"
                download="Plantilla_Importacion_Finanzas_ODS_v1.xlsx"
                className="inline-flex items-center gap-1 text-ods-navigation hover:underline"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Descargar plantilla Excel
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              Para dar de alta proveedores durante la importación, añade una fila por proveedor en la hoja «Proveedores» con las columnas «nombre» y «cif». Se crearán o reutilizarán al confirmar las filas válidas.
            </p>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
              <Input type="file" accept=".xlsx,.csv,.txt" onChange={handleFile} />
              <Button type="button" onClick={handleValidate} disabled={!filePayload || previewMutation.isPending}>
                {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {previewMutation.isPending ? "Validando..." : "Validar archivo"}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {fileName ? <Badge variant="secondary">{fileName}</Badge> : null}
              <Badge variant="outline">No escribe en BD</Badge>
              <Badge variant="outline">Destino obligatorio</Badge>
              <Badge variant="outline">N.º expediente canónico</Badge>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50/80 px-3 py-2 text-sm text-blue-950">
              El preview no escribe datos. Los registros solo se crean cuando confirmas expresamente las filas seleccionadas.
            </div>
            {fileError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {fileError}
              </div>
            ) : null}
            {preview ? <PreviewSummary preview={preview} /> : null}
            {preview && !showCommitConfirm ? (
              <div className="flex flex-col gap-3 rounded-md border px-3 py-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm">
                  <p className="font-medium">Commit auditado</p>
                  <p className="text-muted-foreground">
                    {selectedRows.length} filas limpias seleccionadas. Las filas con avisos o errores no se confirman.
                  </p>
                </div>
                <Button type="button" onClick={() => setShowCommitConfirm(true)} disabled={!selectedRows.length || !filePayload || commitMutation.isPending}>
                  {commitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Confirmar importación auditada
                </Button>
              </div>
            ) : null}
            {showCommitConfirm ? (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 px-4 py-4 space-y-3">
                <p className="text-sm font-medium">¿Confirmar importación?</p>
                <p className="text-sm text-muted-foreground">
                  Se importarán <strong>{selectedRows.length}</strong> filas válidas de <strong>{preview?.summary.totalRows ?? 0}</strong> totales
                  ({preview?.summary.blockedRows ?? 0} bloqueadas). Esta acción registrará los datos en la base de datos y no se puede deshacer automáticamente.
                </p>
                <div className="flex gap-2">
                  <Button type="button" onClick={() => { setShowCommitConfirm(false); commitSelectedRows(); }}>
                    <CheckCircle2 className="h-4 w-4" />
                    Confirmar definitivamente
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCommitConfirm(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : null}
            {commitResult ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-950">
                Batch {commitResult.batchId}: {commitResult.committedRows} filas confirmadas, {commitResult.skippedRows} omitidas.
              </div>
            ) : null}
          </CardContent>
        </Card>

        {preview ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ListChecks className="h-4 w-4" />
                Resultado del preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {visibleIssues.length ? (
                <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
                  <p className="text-sm font-medium text-amber-950">Errores y avisos principales</p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-950">
                    {visibleIssues.map((issue, index) => (
                      <li key={`${issue.rowNumber}-${issue.code}-${index}`}>
                        Fila {issue.rowNumber}: {issue.code} · {issue.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2">Sel.</th>
                      <th className="px-2 py-2">Fila</th>
                      <th className="px-2 py-2">Estado</th>
                      <th className="px-2 py-2">Tipo</th>
                      <th className="px-2 py-2">Destino</th>
                      <th className="px-2 py-2">N.º expediente</th>
                      <th className="px-2 py-2">Proveedor</th>
                      <th className="px-2 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.rowNumber} className="border-b last:border-0">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(row.rowNumber)}
                            disabled={!row.canImportLater || row.issues.length > 0}
                            onChange={() => toggleRow(row.rowNumber)}
                            aria-label={`Seleccionar fila ${row.rowNumber}`}
                          />
                        </td>
                        <td className="px-2 py-2">{row.rowNumber}</td>
                        <td className="px-2 py-2">
                          {row.canImportLater ? (
                            <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Validada</Badge>
                          ) : (
                            <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Bloqueada</Badge>
                          )}
                        </td>
                        <td className="px-2 py-2">{row.kind === "supplier_invoice" ? "Factura" : row.kind === "manual_expense" ? "Gasto" : "Sin tipo"}</td>
                        <td className="px-2 py-2">{FINANCE_IMPORT_DESTINATION_LABELS[row.destinationScope]}</td>
                        <td className="px-2 py-2">
                          {row.installationReference?.expedienteCode ? `Exp. ${row.installationReference.expedienteCode}` : "-"}
                        </td>
                        <td className="px-2 py-2">{row.supplierName ?? "-"}</td>
                        <td className="px-2 py-2">{formatCents(row.totalAmountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4" />
              Historial de importaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {batchesQuery.data?.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2">ID</th>
                      <th className="px-2 py-2">Archivo</th>
                      <th className="px-2 py-2">Confirmadas</th>
                      <th className="px-2 py-2">Omitidas</th>
                      <th className="px-2 py-2">Fecha</th>
                      <th className="px-2 py-2">Rollback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchesQuery.data.map((batch) => (
                      <tr key={batch.id} className="border-b last:border-0">
                        <td className="px-2 py-2 font-mono">#{batch.id}</td>
                        <td className="px-2 py-2">{batch.fileName}</td>
                        <td className="px-2 py-2">{batch.committedRows}</td>
                        <td className="px-2 py-2">{batch.skippedRows}</td>
                        <td className="px-2 py-2">{batch.createdAt ? new Date(batch.createdAt).toLocaleString("es-ES") : "-"}</td>
                        <td className="px-2 py-2 text-xs text-muted-foreground">No disponible</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="px-2 py-4 text-sm text-muted-foreground">Aún no hay importaciones registradas.</p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TableProperties className="h-4 w-4" />
                Plantilla normalizada
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2">
                {sheets.map((sheet) => (
                  <div key={sheet} className="rounded-md border p-3">
                    <p className="text-sm font-medium">{sheet}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Hoja prevista de plantilla App ODS</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4" />
                Reglas de seguridad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {controls.map((control) => (
                  <li key={control} className="flex gap-2">
                    <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{control}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <RotateCcw className="h-4 w-4" />
                Flujo de importación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {previewSteps.map((step, index) => (
                  <div key={step}>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary">{index + 1}</Badge>
                      <span className="text-sm font-medium">{step}</span>
                    </div>
                    {index < previewSteps.length - 1 ? <Separator className="ml-4 my-2" /> : null}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ListChecks className="h-4 w-4" />
                Columnas base de facturas y gastos
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {invoiceColumns.map((column) => (
                <div key={column} className="rounded-md border px-3 py-2 font-mono text-sm">
                  {column}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
